import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { getFilterDefinitionsForHeatmapAnalysis } from 'libs/end-to-end/src/heatmap-analysis';
import { getOverviewExplanationForHeatmapTable } from 'libs/end-to-end/src/heatmap-analysis';
import { getCompatibleDataFromFilteredGrouping } from 'libs/end-to-end/src/heatmap-analysis';
import { mapHeaderCells } from 'libs/end-to-end/src/heatmap-analysis';
import { getMetricLabelsForHeatmapAnalysis } from 'libs/end-to-end/src/heatmap-analysis';
import { HeatmapAnalysisOutput } from 'libs/end-to-end/src/heatmap-analysis';
import { HeatmapFilterGrouping } from 'libs/end-to-end/src/heatmap-analysis';
import { HeatmapDisparityResult } from 'libs/end-to-end/src/heatmap-analysis';
import { HeatmapAnalysisRequest } from 'libs/end-to-end/src/custom-viz/HeatmapAnalysisTypes';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getListValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getDimension } from 'libs/end-to-end/src/dimension-utils';
import { getTreatmentGroup } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getDisparityResultsByDimensionAndTreatmentLevel } from 'libs/end-to-end/src/heatmap-analysis';
import { getFilterGroupingByFilterName } from 'libs/end-to-end/src/heatmap-analysis';
import { ExploreHeader } from 'apps/sigma2-ui/src/explore-section/components/ExploreHeader';
import { TableGridNoData } from 'apps/sigma2-ui/src/explore-section/components/TableGridNoData';
import { ExplorePageFailure } from 'apps/sigma2-ui/src/explore-section/components/ExplorePageFailure';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { filterDisparityResultsByGrouping } from 'libs/end-to-end/src/heatmap-analysis';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ExpandableExplanation } from 'apps/sigma2-ui/src/components/explanations/ExpandableExplanation';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { getFocusAreasInSortedOrder } from 'libs/end-to-end/src/focus-area-utils';
import { ViewModeSelector } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/ViewModeSelector';
import { heatmapViewModeOptions } from 'apps/sigma2-ui/src/explore-section/components/TableViewConfig';
import { HEATMAP_VIEW_MODE } from 'apps/sigma2-ui/src/explore-section/components/TableViewConfig';
import cn from 'classnames';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { isEqual } from 'lodash';

/**
 * ExploreSectionInterface is a complex component that renders a heatmap visualization
 * showing disparities across different metrics and groups.
 *
 * Key features:
 * - Displays a filterable heatmap table with metrics and groups
 * - Supports filter selections through HorizontalFilterBar
 * - Handles share link functionality for saving/loading analysis state
 * - Shows tooltips with detailed information on cell click
 * - Supports metric selection/filtering per focus area
 * - Handles loading states and no-data scenarios
 *
 * @param {MyObservation} observation - The current observation data containing focus areas and filter definitions
 * @param {string} organizationId - The ID of the current organization
 *
 * State management:
 * - selectedCell: Tracks currently selected cell in the heatmap
 * - selectedCellRef: Reference to the currently selected cell
 * - searchTerm: Manages search filtering
 * - selectedFilterDefinition: Current filter definition selection
 * - filteredGrouping: Processed grouping data for display
 * - selectedDisparityResults: Stores current disparity analysis results
 * - tooltipPosition: Manages tooltip positioning
 *
 * Key functionality:
 * - Filter management through useFilters hook
 * - Share link generation and loading
 * - Deep dive navigation
 * - Analytics tracking
 * - Custom visualization handling via useCustomViz
 * - Dimension and treatment group processing
 *
 * Integration points:
 * - Uses useCustomViz for visualization data
 * - Integrates with analytics tracking
 * - Handles user analysis cancellation
 * - Processes share link functionality
 * - Integrates with filter system
 * - Connects with dimension configuration
 *
 * Component structure:
 * - HorizontalFilterBar for filter selection
 * - TableGridHeader for filter definition selection
 * - TableGrid for main visualization
 * - TableGridTooltip for detailed cell information
 * - Loading and no-data states
 */
interface ExploreSectionInterfaceProps {
  observation: MyObservation;
  organizationId: string;
}

export const getExplorePrimaryFilterSelectionsFromRequest = ({
  request,
  getLevelByColumnValue,
  getPrivilegedLevelForDimension,
}: {
  request: Pick<HeatmapAnalysisRequest, 'inputs'>;
  getPrivilegedLevelForDimension: UseDimensionConfigPayload['getPrivilegedLevelForDimension'];
  getLevelByColumnValue: UseDimensionConfigPayload['getLevelByColumnValue'];
}): PrimaryFilterSelection[] => {
  const primaryFilterSelections: PrimaryFilterSelection[] = [];

  const dimension = request.inputs.extras.dimension;

  const referenceGroup = getPrivilegedLevelForDimension(dimension);

  const treatmentGroup = getLevelByColumnValue(
    dimension,
    request.inputs.extras.treatmentDimensionLevel ?? '',
  );

  if (treatmentGroup) {
    primaryFilterSelections.push({
      primaryFilterKey: 'employeeGroup',
      value: [dimension, treatmentGroup.columnValue],
    });
  }

  if (referenceGroup) {
    primaryFilterSelections.push({
      primaryFilterKey: 'comparisonGroup',
      value: referenceGroup.columnValue,
    });
  }

  const metrics = request.inputs.extras.metrics ?? [];

  if (metrics.length > 0) {
    primaryFilterSelections.push({
      primaryFilterKey: 'metrics',
      value: metrics,
    });
  }

  return primaryFilterSelections;
};

export const ExploreSectionInterface: React.FC<
  ExploreSectionInterfaceProps
> = ({ observation, organizationId }) => {
  const { userAuthenticatedApiCall, myUser } = useUser();
  const context = useCustomViz();
  const { doViz } = context;
  const shareLinkRawData = useShareLinkSearchParameter();
  const analytics = useAnalytics();
  const dimensionsConfigPayload = useDimensionConfig();

  const [viewConfiguration, setViewConfiguration] =
    useState<ViewConfigurationSchema>({
      selectedFilterDefinition: '',
      defaultViewMode: HEATMAP_VIEW_MODE,
    });

  // State
  const [currentRequestForComparison, setCurrentRequestForComparison] =
    useState<HeatmapAnalysisRequest | null>(null);

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): {
      customVizRequest: HeatmapAnalysisRequest | undefined;
      viewConfiguration?: ViewConfigurationSchema;
    } => {
      const treatmentGroup = getTreatmentGroup(primaryFilterSelections);
      const dimension = getDimension(primaryFilterSelections);

      const metrics =
        getListValueFromPrimaryFilterValues({
          primaryFilterKey: 'metrics',
          primaryFilterValues: primaryFilterSelections,
        }) ?? [];

      if (!treatmentGroup || !dimension)
        return { customVizRequest: undefined, viewConfiguration: undefined };

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: HeatmapAnalysisRequest = {
        customVizId: 'heatmap',
        organizationId,
        observationLocationType: observation.type,
        inputs: {
          extras: {
            dimension,
            filters,
            treatmentDimensionLevel: treatmentGroup,
            metrics,
            filterDefinition: viewConfiguration.selectedFilterDefinition,
          },
          dropdowns: {},
        },
      };

      return {
        customVizRequest,
        viewConfiguration,
      };
    },
    [observation.type, organizationId, viewConfiguration],
  );

  // Then use data in the callback
  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: {
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
    }) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
      const treatmentGroup = getTreatmentGroup(primaryFilterSelections);
      const dimension = getDimension(primaryFilterSelections);

      if (!treatmentGroup || !dimension || !customVizRequest) return;

      const removeCosmeticItems = (
        r: HeatmapAnalysisRequest,
      ): HeatmapAnalysisRequest => {
        return {
          ...r,
          inputs: {
            ...r.inputs,
            extras: {
              ...r.inputs.extras,
              // exclude the parts that don't impact needing to request again
              treatmentDimensionLevel: undefined,
              filterDefinition: undefined,
            },
          },
        };
      };

      if (
        !currentRequestForComparison ||
        !isEqual(
          removeCosmeticItems(customVizRequest),
          removeCosmeticItems(currentRequestForComparison),
        )
      ) {
        doViz(customVizRequest);
        setCurrentRequestForComparison(customVizRequest);
      }
    },
    [getCustomVizRequest, currentRequestForComparison, doViz],
  );

  // Filter setup
  const filterPayload = useFilters({
    feature: 'exploreHeatmap',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    analysisName: undefined,
    onApply: useCallback(
      ({
        primaryFilterSelections,
        secondaryFilterSelections,
        isProgrammaticChange,
        isInitialApply,
      }: {
        primaryFilterSelections: PrimaryFilterSelection[];
        secondaryFilterSelections: SecondaryFilterSelection[];
        isProgrammaticChange: boolean;
        isInitialApply: boolean;
      }) => {
        // This prevents onShareLinkLoaded from triggering two custom viz
        // requests. Without this, calling setFilters triggers a new custom viz
        // But in the case of a share link, we need to manually call the custom
        // viz so we can pass the metric, which these filters don't know about
        if (isProgrammaticChange) {
          return;
        }

        // This prevents from loading the initial filters if a share link is applied to the page.
        if (isInitialApply && shareLinkRawData !== null) {
          return;
        }

        onAnalyzeWithParams({
          primaryFilterSelections,
          secondaryFilterSelections,
        });
      },
      [onAnalyzeWithParams, shareLinkRawData],
    ),
    getCustomVizRequest,
  });

  // Share link handling
  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (!request || !observation || request.customVizId !== 'heatmap') return;

      const filters = request.inputs.extras.filters;
      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'exploreHeatmap',
          focusArea: null,
          customVizFilters: filters,
          focusAreas: observation.focusAreas,
          filterSetDefinitionsWithValues:
            observation.filterSetDefinitionsWithValues,
          dimensionDefinitions: observation.dimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            observation.pipelineAnalysisConfiguration,
        });

      const primaryFilterSelections =
        getExplorePrimaryFilterSelectionsFromRequest({
          request,
          getPrivilegedLevelForDimension:
            dimensionsConfigPayload.getPrivilegedLevelForDimension,
          getLevelByColumnValue: dimensionsConfigPayload.getLevelByColumnValue,
        });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, observation, onAnalyzeWithParams, dimensionsConfigPayload],
  );

  // Share link request
  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: HeatmapAnalysisRequest,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  // Effects
  // Track event when the page is viewed
  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Explore Page Viewed',
      eventProperties: {
        selectedFocusArea: null,
        selectedTab: 'Explore',
      },
    });
  }, [analytics]);

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <HorizontalFilterBar
        items={filterPayload.items}
        slotRight={
          <DataRefreshIndicator
            status={context.status}
            resultCreatedAt={context.resultCreatedAt}
          />
        }
      />

      {lastCustomVizRequest && (
        <div className="rounded-lg w-full shadow-2px bg-white min-h-96">
          <ExploreSectionCard
            customVizRequest={lastCustomVizRequest}
            result={context.result}
            status={context.status}
            viewConfiguration={viewConfiguration}
            setViewConfiguration={setViewConfiguration}
          />
        </div>
      )}
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />
    </Spacing>
  );
};

// eslint-disable-next-line complexity
export const ExploreSectionCard = ({
  customVizRequest,
  result,
  status,
  viewConfiguration,
  setViewConfiguration,
}: {
  customVizRequest: HeatmapAnalysisRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  viewConfiguration: ViewConfigurationSchema;
  setViewConfiguration?: (viewConfiguration: ViewConfigurationSchema) => void;
}) => {
  const { myObservation: observation } = useObservation();

  const language = useLanguage();

  const dimensionsConfigPayload = useDimensionConfig();

  const visibleMetrics = customVizRequest?.inputs.extras.metrics;

  const {
    hideSearch,
    selectedMetric,
    baseColors,
    selectedFilterDefinition,
    defaultViewMode = HEATMAP_VIEW_MODE,
  } = viewConfiguration;

  // State

  const [filteredGrouping, setFilteredGrouping] =
    useState<HeatmapFilterGrouping | null>(null);

  const [selectedDisparityResults, setSelectedDisparityResults] =
    useState<HeatmapDisparityResult | null>(null);

  const [failed, setFailed] = useState(false);

  // Parse custom viz result first
  const { data, readyToDisplayDynamicContent, hasFailed } = useMemo(() => {
    return parseCustomVizResult({
      result: result,
      status: status,
      ResultZodType: HeatmapAnalysisOutput,
    });
  }, [result, status]);

  // Get dimension and treatment group
  const dimension = useMemo(
    () => customVizRequest?.inputs.extras.dimension,
    [customVizRequest?.inputs.extras.dimension],
  );

  const dimensionDefinition = useMemo(() => {
    if (!dimension) return null;
    return (
      dimensionsConfigPayload.getDimensionByColumnHeaderValue(dimension) ?? null
    );
  }, [dimensionsConfigPayload, dimension]);

  const treatmentGroup = useMemo(() => {
    return customVizRequest?.inputs.extras.treatmentDimensionLevel;
  }, [customVizRequest?.inputs.extras.treatmentDimensionLevel]);

  const treatmentGroupLevelDefinition = useMemo(() => {
    if (!dimension || !treatmentGroup) return null;
    return (
      dimensionsConfigPayload.getLevelByColumnValue(
        dimension,
        treatmentGroup,
      ) ?? null
    );
  }, [dimensionsConfigPayload, treatmentGroup, dimension]);

  const referenceGroupLevelDefinition = useMemo(() => {
    if (!dimension) return null;
    return (
      dimensionsConfigPayload.getPrivilegedLevelForDimension(dimension) ?? null
    );
  }, [dimensionsConfigPayload, dimension]);

  // Filter definitions for the explore analysis
  const filterDefinitions = useMemo(() => {
    if (!selectedDisparityResults) return [];

    return getFilterDefinitionsForHeatmapAnalysis({
      myObservation: observation,
      selectedDisparityResults,
    });
  }, [selectedDisparityResults, observation]);

  // Effects
  useEffect(() => {
    // Type guard to ensure data exists
    if (!data?.controlledDisparities?.disparityResults) {
      return;
    }

    const filteredDisparityResultsHeatmap = filterDisparityResultsByGrouping(
      data.controlledDisparities?.disparityResults,
    );

    if (filteredDisparityResultsHeatmap) {
      // Use existing results if filters haven't changed
      const disparityResults = getDisparityResultsByDimensionAndTreatmentLevel({
        disparityResults: filteredDisparityResultsHeatmap,
        dimension,
        treatmentLevel: treatmentGroup,
      });
      setSelectedDisparityResults(disparityResults);
    }
  }, [
    data?.controlledDisparities?.disparityResults,
    dimension,
    treatmentGroup,
  ]);

  useEffect(() => {
    setFailed(hasFailed);
  }, [hasFailed]);

  // When the heatmap disparity results change, update the selected disparity results, selected view, and filtered grouping
  useEffect(() => {
    try {
      if (!data?.controlledDisparities?.disparityResults) return;

      // For each filter grouping, filter out the filter grouping that have department as the filter name
      const filteredDisparityResultsHeatmap = filterDisparityResultsByGrouping(
        data.controlledDisparities?.disparityResults,
      );

      if (
        !dimensionDefinition?.columnHeaderValue ||
        !treatmentGroupLevelDefinition
      ) {
        setFailed(true);
        setSelectedDisparityResults(null);
        setViewConfiguration?.({
          selectedFilterDefinition: '',
          defaultViewMode: HEATMAP_VIEW_MODE,
        });
        setFilteredGrouping(null);
        return;
      }

      const response = getDisparityResultsByDimensionAndTreatmentLevel({
        disparityResults: filteredDisparityResultsHeatmap,
        dimension: dimensionDefinition.columnHeaderValue,
        treatmentLevel: treatmentGroupLevelDefinition.columnValue,
      });

      if (!response || !response.disparityResult.filterGroupings?.length) {
        setSelectedDisparityResults(null);
        setViewConfiguration?.({
          selectedFilterDefinition: '',
          defaultViewMode: HEATMAP_VIEW_MODE,
        });
        setFilteredGrouping(null);
        return;
      }

      setSelectedDisparityResults(response);

      if (!viewConfiguration.selectedFilterDefinition) {
        setViewConfiguration?.({
          defaultViewMode: HEATMAP_VIEW_MODE,
          selectedFilterDefinition:
            response.disparityResult.filterGroupings[0].filterName,
        });
      }
      // The filtered grouping will be set in the useEffect below
    } catch {
      setFailed(true);
      setSelectedDisparityResults(null);
      setViewConfiguration?.({
        defaultViewMode: HEATMAP_VIEW_MODE,
        selectedFilterDefinition: '',
      });
      setFilteredGrouping(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dimensionDefinition, treatmentGroupLevelDefinition]);

  // When the disparity results and selected filter definition are selected, update the filtered grouping
  useEffect(() => {
    if (
      selectedDisparityResults &&
      viewConfiguration.selectedFilterDefinition
    ) {
      const metricLabels = getMetricLabelsForHeatmapAnalysis(observation);

      const groupingArray = getFilterGroupingByFilterName({
        selectedDisparityResults,
        selectedFilterName: viewConfiguration.selectedFilterDefinition,
        metricLabels,
      });

      const grouping = groupingArray.length === 0 ? null : groupingArray[0];

      if (grouping) {
        setFilteredGrouping(grouping);

        if (
          grouping.filterName !== viewConfiguration.selectedFilterDefinition
        ) {
          setViewConfiguration?.({
            defaultViewMode: HEATMAP_VIEW_MODE,
            selectedFilterDefinition: grouping.filterName,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDisparityResults,
    observation,
    viewConfiguration.selectedFilterDefinition,
  ]);

  const sortedFocusAreas = getFocusAreasInSortedOrder({
    focusAreas: observation?.focusAreas || [],
  }).filter((fa) =>
    Boolean(
      filteredGrouping?.filterData.metrics.some((resultMetric) => {
        return resultMetric.focusArea === fa.focusArea;
      }),
    ),
  );

  /** Filter visible metrics */
  const filteredMetrics = useMemo(
    () =>
      filteredGrouping?.filterData.metrics.filter((metric) =>
        visibleMetrics ? visibleMetrics.includes(metric.metric) : true,
      ) ?? [],
    [filteredGrouping?.filterData.metrics, visibleMetrics],
  );

  const headCells = useMemo(() => {
    return mapHeaderCells({
      sortedFocusAreas: sortedFocusAreas.map(
        (focusArea) => focusArea.focusArea,
      ),
      filteredMetrics,
    });
  }, [filteredMetrics, sortedFocusAreas]);

  // Create compatibility layer for existing components
  const compatibleData = useMemo(() => {
    if (!filteredGrouping) return null;

    const baseData = getCompatibleDataFromFilteredGrouping({
      filteredGrouping,
      sortedFocusAreas: sortedFocusAreas.map(
        (focusArea) => focusArea.focusArea,
      ),
      visibleMetrics: visibleMetrics ?? [],
      headCells,
    });

    return {
      ...baseData,
      selectedGroupBy: treatmentGroup || undefined,
      explanations: data?.explanations,
      filtersToBreakdown: filterDefinitions,
      numeratorSizes: [],
    };
  }, [
    filteredGrouping,
    sortedFocusAreas,
    visibleMetrics,
    headCells,
    treatmentGroup,
    data?.explanations,
    filterDefinitions,
  ]);

  const showNewExplanations = useMemo(
    () => observation?.showNewExplanations ?? false,
    [observation?.showNewExplanations],
  );

  const overviewExplanation = useMemo(() => {
    if (
      !treatmentGroup ||
      !treatmentGroupLevelDefinition ||
      !referenceGroupLevelDefinition ||
      !data ||
      !observation
    ) {
      return {
        overview: '',
        description: '',
      };
    }

    return getOverviewExplanationForHeatmapTable({
      explanations: data?.explanations,
      myObservation: observation,
      dimension: dimension || '',
      referenceGroup: referenceGroupLevelDefinition?.columnValue || '',
      treatmentGroup,
      language,
    });
  }, [
    data,
    dimension,
    language,
    observation,
    referenceGroupLevelDefinition,
    treatmentGroup,
    treatmentGroupLevelDefinition,
  ]);

  const explanationHeading = useMemo(() => {
    if (
      !dimensionDefinition ||
      !treatmentGroupLevelDefinition ||
      !referenceGroupLevelDefinition
    ) {
      return '';
    }

    return language.exploreHeatmapAnalysisExplanationHeading({
      treatmentGroupLevelDefinition,
      referenceGroupLevelDefinition,
    });
  }, [
    dimensionDefinition,
    treatmentGroupLevelDefinition,
    referenceGroupLevelDefinition,
    language,
  ]);

  const handleFilterDefinitionChange = useCallback(
    (filterDefinition: string) => {
      setViewConfiguration?.({
        defaultViewMode: HEATMAP_VIEW_MODE,
        selectedFilterDefinition: filterDefinition,
      });
    },
    [setViewConfiguration],
  );

  const viewModeOption = useMemo(() => {
    const viewModeOption = heatmapViewModeOptions.find(
      (option) => option.value === viewConfiguration?.defaultViewMode,
    );
    const defaultViewModeOption = heatmapViewModeOptions.find(
      (option) => option.value === HEATMAP_VIEW_MODE,
    );
    return viewModeOption ? viewModeOption : defaultViewModeOption;
  }, [viewConfiguration?.defaultViewMode]);

  const ViewModeComponent = useMemo(() => {
    return viewModeOption ? viewModeOption.component : null;
  }, [viewModeOption]);

  const visibleRows = useMemo(() => {
    return compatibleData?.rows.map((_, index) => index).slice(-50) ?? [];
  }, [compatibleData?.rows]);

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const defaultMetric = useMemo(() => {
    if (!compatibleData) {
      return undefined;
    }
    const metricInfo = getMetricAndFocusAreaFromColumnName({
      columnName: compatibleData.columnHeaders[0],
    });
    return metricInfo?.metric.columnHeaderValue;
  }, [compatibleData, getMetricAndFocusAreaFromColumnName]);

  return (
    <div className="p-6 h-full w-full">
      {failed && !readyToDisplayDynamicContent ? (
        <ExplorePageFailure />
      ) : !readyToDisplayDynamicContent ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          h="full"
          w="full"
          flex="1"
        >
          <CustomVizCircularSpinner />
        </Box>
      ) : (
        <>
          {showNewExplanations && (
            <ExpandableExplanation
              headline={explanationHeading}
              overview={overviewExplanation.overview}
              details={overviewExplanation.description}
            />
          )}

          {compatibleData && (
            <div className={cn('flex items-center gap-2 pb-3.5 bg-white')}>
              <ViewModeSelector
                defaultViewMode={HEATMAP_VIEW_MODE}
                viewConfiguration={viewConfiguration}
                setViewConfiguration={setViewConfiguration}
                options={heatmapViewModeOptions}
              />

              {viewModeOption?.filters.map((filter) => (
                <filter.component
                  key={filter.id}
                  data={compatibleData}
                  viewConfiguration={viewConfiguration ?? {}}
                  setViewConfiguration={setViewConfiguration}
                />
              ))}
            </div>
          )}

          {selectedFilterDefinition &&
            defaultViewMode === HEATMAP_VIEW_MODE && (
              <ExploreHeader
                selectedFilterDefinition={selectedFilterDefinition}
                onSelectFilterDefinition={handleFilterDefinitionChange}
                filterDefinitions={filterDefinitions}
                filteredGrouping={filteredGrouping}
              />
            )}

          {!compatibleData ? (
            <TableGridNoData />
          ) : (
            ViewModeComponent && (
              <ViewModeComponent
                data={compatibleData}
                visibleRows={visibleRows}
                viewConfiguration={{
                  defaultViewMode: viewConfiguration.defaultViewMode,
                  selectedFilterDefinition:
                    viewConfiguration.selectedFilterDefinition,
                  baseColors,
                  hideSearch: hideSearch ?? false,
                  displayBorder: false,
                  selectedMetric: selectedMetric ?? defaultMetric,
                  units: 'percentage',
                }}
              />
            )
          )}
        </>
      )}
    </div>
  );
};
